import { AppError } from "./errors";
import { GOOGLE_BUNDLE_BASE_SCOPE_BUNDLE } from "./googleConnectResolution";
import { pickStringByPaths } from "./path";

export const NEWS_GMAIL_SEND_SCOPE_BUNDLE = "news_gmail_send";
export const NEWS_GMAIL_SEND_SCOPE_GUIDE =
  "Google連携（Gmail送信権限）の追加同意が必要です。追加同意後に再度お試しください。";

export type NewsJobConflictDisplay = {
  message?: string;
  detail?: string;
  requiredScopeBundle?: string;
  connectAuthorizationUrl?: string;
  shouldShowGoogleReconnect?: boolean;
};

function extractLegacyConnectToken(value?: string): string | undefined {
  if (!value) return undefined;

  const tokenBeforeExpires = value.match(/([A-Za-z0-9_-]{16,})["']?\s*,?\s*expires_in_seconds/i);
  if (tokenBeforeExpires?.[1]) {
    return tokenBeforeExpires[1];
  }

  const tokens = value.match(/[A-Za-z0-9_-]{16,}/g);
  if (!tokens || tokens.length === 0) return undefined;

  return tokens[tokens.length - 1];
}

function buildLegacyConnectUrl(error: AppError, token: string): string | undefined {
  const encodedToken = encodeURIComponent(token);

  const responseUrl = error.meta.responseUrl?.trim();
  if (responseUrl) {
    try {
      const parsed = new URL(responseUrl);
      const basePath = parsed.pathname.replace(/\/+$/, "");
      if (basePath.endsWith("/api/v1/news/jobs")) {
        return `${parsed.origin}${basePath}/${encodedToken}`;
      }
      if (basePath.endsWith("/api/v1/news/jobs/connect")) {
        return `${parsed.origin}${basePath}?state=${encodedToken}`;
      }
    } catch {
      // noop
    }
  }

  const endpoint = error.meta.endpoint?.trim();
  if (endpoint && typeof window !== "undefined" && window.location?.origin) {
    const basePath = endpoint.replace(/\/+$/, "");
    if (basePath.endsWith("/api/v1/news/jobs")) {
      return `${window.location.origin}${basePath}/${encodedToken}`;
    }
    if (basePath.endsWith("/api/v1/news/jobs/connect")) {
      return `${window.location.origin}${basePath}?state=${encodedToken}`;
    }
  }

  return undefined;
}

function parseAppErrorResponseBody(error: AppError): any | null {
  const responseText = error.meta.responseText?.trim();
  if (!responseText) return null;

  try {
    return JSON.parse(responseText);
  } catch {
    return null;
  }
}

export function resolveNewsJobConflictDisplay(error: unknown): NewsJobConflictDisplay | null {
  if (!(error instanceof AppError)) return null;
  if (error.code !== "CONFLICT" || error.meta.status !== 409) return null;

  const body = parseAppErrorResponseBody(error);
  if (!body || typeof body !== "object") return {};

  const message = pickStringByPaths(body, ["message", "data.message"]);
  const responseRequiredScopeBundle = pickStringByPaths(body, [
    "detail.required_scope_bundle",
    "detail.requiredScopeBundle",
    "data.detail.required_scope_bundle",
    "data.detail.requiredScopeBundle",
  ]);
  // TODO: 個別スコープ再同意が安定したら responseRequiredScopeBundle を優先する
  const requiredScopeBundle = GOOGLE_BUNDLE_BASE_SCOPE_BUNDLE;
  const connectAuthorizationUrl = pickStringByPaths(body, [
    "detail.connect.authorization_url",
    "detail.connect.authorizationUrl",
    "detail.connect_url",
    "detail.connectUrl",
    "connect.authorization_url",
    "connect.authorizationUrl",
    "connect_url",
    "connectUrl",
    "authorization_url",
    "authorizationUrl",
    "data.detail.connect.authorization_url",
    "data.detail.connect.authorizationUrl",
    "data.detail.connect_url",
    "data.detail.connectUrl",
    "data.connect.authorization_url",
    "data.connect.authorizationUrl",
    "data.connect_url",
    "data.connectUrl",
    "data.authorization_url",
    "data.authorizationUrl",
  ]);
  const connectTokenFromMessage = !connectAuthorizationUrl
    ? extractLegacyConnectToken(message)
    : undefined;
  const connectUrl =
    connectAuthorizationUrl ??
    (connectTokenFromMessage ? buildLegacyConnectUrl(error, connectTokenFromMessage) : undefined);
  const shouldShowGoogleReconnect = !!connectUrl;

  return {
    message,
    detail: shouldShowGoogleReconnect ? NEWS_GMAIL_SEND_SCOPE_GUIDE : undefined,
    requiredScopeBundle: responseRequiredScopeBundle ? requiredScopeBundle : undefined,
    connectAuthorizationUrl: connectUrl,
    shouldShowGoogleReconnect,
  };
}
